import React, { useEffect, useRef } from 'react'
import { Stack, useRouter, usePathname, useGlobalSearchParams } from 'expo-router'
import { useAuth } from '../../hooks/useAuth'
import { useExpoPushSync } from '../../hooks/useExpoPushSync'
import { queryClient } from '../../lib/queryClient'
import { apiGet } from '../../lib/api'
import { getPendingRedirect, setPendingRedirect, clearPendingRedirect } from '../../lib/pendingRedirect'
import { buildProtectedFullPath, resolvePostAuthRedirect } from '../../lib/deepLinkHelpers'
import { reportError } from '../../lib/reporting'
import type { MapSummary } from '../../hooks/useMaps'

// The non-test map list is ~200 bytes and never changes at runtime.
// Pre-warming the cache here eliminates the gallery spinner when the
// user first taps "New Game" — by the time they reach the picker the
// list is already in cache with staleTime: Infinity.
// One list, one key — whether test maps are in it is the server's call, so
// there is no second variant to prefetch (see hooks/useMaps.ts).
function prefetchMapList() {
  void queryClient.prefetchQuery({
    queryKey: ['maps', 'list'],
    queryFn: () => apiGet<MapSummary[]>('/api/v1/maps'),
    staleTime: Infinity,
  })
}

export default function AppLayout() {
  const { isAuthenticated, isExplicitLogout, isLoading } = useAuth()
  const router = useRouter()
  const pathname = usePathname()
  const { block, messageId, kind, gameId } = useGlobalSearchParams<{ block?: string | string[]; messageId?: string; kind?: string; gameId?: string }>()
  const blockTarget = Array.isArray(block) ? block[0] : block

  // Register Expo push token with the API once per authenticated session.
  // The hook is mounted here (inside the authenticated layout) so it only
  // ever runs for signed-in users. It no-ops on web and guards internally
  // with a ref so it fires exactly once per mount regardless of re-renders.
  useExpoPushSync()

  // Pre-warm the map list cache as soon as auth resolves. The list is
  // static, tiny (~200 bytes), and needed whenever the user opens New Game.
  // Firing it here (not inside useMaps) avoids the visible spinner on the
  // first New Game tap.
  useEffect(() => {
    if (isAuthenticated) {
      prefetchMapList()
    }
    // Re-prefetch if the user logs in mid-session (guest → signed-in).
  }, [isAuthenticated])

  // ── The one auth-redirect spot ───────────────────────────────────────────
  // Bounce: an unauthenticated visitor to any protected (app) route is sent to
  // login, preserving where they were headed. We persist that target (survives
  // the sign-UP → email-confirmation → login hop, which drops the ?redirect
  // param) AND pass it as ?redirect for the fast direct-sign-in path.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Explicit logout is different from an unauthenticated deep link. The
      // current protected pathname (often /settings) is still mounted while
      // logout clears auth, so capturing it here would send the next login
      // straight back to the page the user intentionally left.
      if (isExplicitLogout) {
        void clearPendingRedirect().catch((err: unknown) => {
          reportError(err, { tags: { surface: 'auth-redirect', action: 'clear-after-logout' } })
        })
        router.replace('/(auth)/login')
        return
      }
      const fullPath = buildProtectedFullPath(pathname, blockTarget, messageId, kind, gameId)
      void setPendingRedirect(fullPath)
      router.replace(`/(auth)/login?redirect=${encodeURIComponent(fullPath)}`)
    }
  }, [isAuthenticated, isExplicitLogout, isLoading, router, pathname, blockTarget, messageId, kind, gameId])

  // Resume: the moment the viewer is authenticated inside (app) — whether via a
  // direct sign-in or the multi-step confirmation hop — consume any persisted
  // target and navigate there. Fires once per mount; validated against the
  // internal-route allowlist (open-redirect safety) before use.
  const resumedRef = useRef(false)
  useEffect(() => {
    if (isLoading || !isAuthenticated || resumedRef.current) return
    resumedRef.current = true
    void (async () => {
      const target = await getPendingRedirect()
      if (!target) return
      await clearPendingRedirect()
      const dest = resolvePostAuthRedirect(target, buildProtectedFullPath(pathname, blockTarget, messageId, kind, gameId))
      if (dest) router.replace(dest as Parameters<typeof router.replace>[0])
    })()
  }, [isAuthenticated, isLoading, router, pathname, blockTarget, messageId, kind, gameId])

  // gestureEnabled:false — swipe-back is disabled app-wide (see app/_layout).
  return <Stack screenOptions={{ headerShown: false, gestureEnabled: false }} />
}
